/* global console, $ window setTimeout */
import * as React from "react";
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { makeStyles } from "@fluentui/react-components";
import { useDispatch, useSelector, useStore } from "react-redux";
import Gridd from "./Gridd";
import Templates from "./Templates";
import Startup from "./Startup";
import Walkthrough from "./Walkthrough";
import { withTranslation } from "react-i18next";
import StyleSheetEditor from "./views/StyleSheetEditor";
import { normalize, select, reset } from "../state/selectionSlice";
import { ActionCreators as UndoActionCreators } from "redux-undo";
import { saveGriddToFile } from "../helpers/fileSystem";
import GlobalToast from "./providers/GlobalToast";
import MousePositionTracker from "./MousePositionTracker";

// Dialogs/modals are only shown on user interaction, so they are code-split
// out of the initial bundle to reduce initial parse/evaluate time.
const StyleSheetEditorDialog = React.lazy(() => import("./views/StyleSheetEditorDialog"));
const PreferencesDialog = React.lazy(() => import("./views/PreferencesDialog"));
const ImageCropDialog = React.lazy(() => import("./views/ImageCropDialog"));
const WelcomePopup = React.lazy(() => import("./views/WelcomePopup"));
const CopyPasteDialog = React.lazy(() => import("./views/CopyPasteDialog"));
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import i18n from "../i18n/i18n"; // NOSONAR
import PropTypes from "prop-types";
import { DeckAICommsProvider } from "./providers/DeckAIComms";
import { setPresentationId, toStartUp } from "../state/appStateSlice";
import { safeGetCurrentSlideId } from "../ppt/safePPT";
import exists from "../helpers/exists";

const useClasses = makeStyles({
  root: {
    display: "flex",
    alignItems: "stretch",
    width: "100%",
    height: "100%",
  },
});

const App = ({ presId, slideId }) => {
  const classes = useClasses();
  const dispatch = useDispatch();
  const store = useStore();
  const location = useLocation();
  const gridRef = useRef(null);
  // Only fields used in render are subscribed. Handler-only/volatile state
  // (structure for the select-all shortcut, currentSlideId for the focus check)
  // is read via store.getState() at call time, and the previously-subscribed
  // hover/selection values are dropped entirely — they only padded effect deps
  // and made App re-render (and rebind listeners) on every hover.
  const { view, styleSheetDialogOpen, preferencesDialogOpen, imageCropDialogOpen, welcomePopupOpen } = useSelector(
    (state) => state.app
  );

  useEffect(() => {
    dispatch(setPresentationId({ presentationId: presId, slideId }));
  }, []);

  useEffect(() => {
    const onFocus = async () => {
      // const current = await getCurrentSlideId();
      // console.log("App is in focus", {
      //   href: window.location.href,
      //   lostFocus: window.lostFocus,
      //   current,
      //   presId,
      //   slideId,
      // });

      // This focus handler exists to re-sync the taskpane with PowerPoint's
      // currently selected slide (and to reset after a long idle). It only
      // makes sense with a real Office host. In embedded mode (embeddable.html
      // in an iframe, no PowerPoint object) safeGetCurrentSlideId returns a
      // constant placeholder slide, so this logic would spuriously self-reload
      // the embed on every focus — skip it entirely there.
      if (!exists("PowerPoint")) return;

      if (window.lostFocus) {
        const diff = new Date() - new Date(window.lostFocus);
        if (diff > 60 * 1000) {
          dispatch(toStartUp());
        }
      }
      window.lostFocus = undefined;

      setTimeout(async () => {
        const current = await safeGetCurrentSlideId();
        // console.log("App stayed in focus", {
        //   current,
        //   presId,
        //   slideId,
        // });
        const currentSlideId = store.getState().app.currentSlideId;
        if (slideId !== current.slideId && currentSlideId !== current.slideId) {
          //notify("error", "Slide misloaded, reloading");
          dispatch(toStartUp(current.slideId));
        }
      }, 150);
    };

    const onBlur = () => {
      // console.log("App lost focus");
      window.lostFocus = new Date();
    };

    window.addEventListener("focus", onFocus);
    window.addEventListener("blur", onBlur);

    return () => {
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("blur", onBlur);
    };
  }, [dispatch, slideId, store]);

  useEffect(() => {
    function onKeyUp(e) {
      if (e.ctrlKey || e.metaKey) {
        console.log("special binding", { ctrl: e.ctrlKey, meta: e.metaKey, e });
        switch (e.key) {
          case "a": {
            e.preventDefault();
            e.stopPropagation();
            const { rows, columns, cells } = store.getState().gridd.present.structure;
            dispatch(reset());
            dispatch(select({ table: true }));
            dispatch(normalize({ rowCount: rows.length, colCount: columns.length, cells }));
            break;
          }
          case "z": {
            dispatch(UndoActionCreators.undo());
            e.preventDefault();
            e.stopPropagation();
            break;
          }
          case "y": {
            dispatch(UndoActionCreators.redo());
            e.preventDefault();
            e.stopPropagation();
            break;
          }
          case "s": {
            const storeState = store.getState();
            if (typeof PowerPoint === "undefined" && storeState.app.localStorageKey) {
              saveGriddToFile(storeState);
            }
            e.preventDefault();
            e.stopPropagation();
            break;
          }
        }
      }
    }

    $("body").on("keyup", onKeyUp);
    return () => {
      $("body").off("keyup", onKeyUp);
    };
  }, [dispatch, store]);

  const renderAppView = (view) => {
    switch (view) {
      case "gridd":
        return <Gridd gridRef={gridRef} presId={presId} slideId={slideId} />;
      case "startup":
        return <Startup presId={presId} slideId={slideId} />;
      default:
        return <Templates />;
    }
  };

  return location.search.indexOf("styleSheet") !== -1 ? (
    <StyleSheetEditor isInDialog={false} />
  ) : (
    <DeckAICommsProvider>
      <div className={classes.root}>
        <MousePositionTracker />
        <GlobalToast />
        <React.Suspense fallback={null}>
          {/* Mounted only while open, so their chunks load on first interaction.
              CopyPasteDialog stays mounted: its paste runs in an effect that
              fires after it dispatches its own close, so unmounting on close
              would drop the paste. */}
          {styleSheetDialogOpen && <StyleSheetEditorDialog />}
          {preferencesDialogOpen && <PreferencesDialog />}
          {imageCropDialogOpen && <ImageCropDialog />}
          {welcomePopupOpen && <WelcomePopup />}
          <CopyPasteDialog />
        </React.Suspense>
        <Walkthrough />
        {renderAppView(view)}
      </div>
    </DeckAICommsProvider>
  );
};

App.propTypes = {
  presId: PropTypes.string,
  slideId: PropTypes.string,
};

export default withTranslation()(App);
